// src/components/layout/BottomNav.tsx — SCE Issues
// Bottom navigation avec FAB central + 2 items de chaque côté
//
// Disposition : [Accueil] [Équipements] [FAB +] [Toutes] [Stats]
// Grille 5 colonnes — FAB visuellement centré au milieu exact.
//
// ─── Pourquoi Équipements et plus « Mes déclarations » ───
//
// Le référentiel était enterré dans le tiroir latéral, lui-même supprimé.
// « Mes déclarations » reprend sa place sur l'accueil, où il a désormais sa
// propre section : c'est la première page qu'on ouvre, la destination y est
// plus directe qu'au fond d'un menu. La route /my-issues existe toujours,
// atteinte par le « Tout voir » de cette section.
//
// La forme de la barre ne bouge pas : cinq colonnes, « + » au centre exact.

import type { CSSProperties } from 'react'
import { NavLink } from 'react-router-dom'
import { useChoixDeclaration } from '@/components/layout/ChoixDeclaration'
import {
  Home,
  Boxes,
  List,
  Plus,
  BarChart3,
} from 'lucide-react'

interface NavItem {
  to: string
  label: string
  Icon: typeof Home
}

// 4 items de navigation — 2 à gauche du FAB, 2 à droite
const ITEMS_LEFT: NavItem[] = [
  { to: '/', label: 'Accueil', Icon: Home },
  { to: '/equipements', label: 'Équipements', Icon: Boxes },
]

const ITEMS_RIGHT: NavItem[] = [
  { to: '/issues', label: 'Toutes', Icon: List },
  { to: '/dashboard', label: 'Stats', Icon: BarChart3 },
]

/**
 * Bottom navigation mobile avec FAB central "+".
 *
 * Pattern validé : le bouton + est surélevé (-12px) pour le différencier
 * visuellement et privilégier la déclaration rapide (l'objectif produit).
 *
 * Grille : 5 colonnes égales. Le FAB occupe la colonne centrale,
 * exactement au milieu du viewport.
 */
export function BottomNav(): JSX.Element {
  // Le + ouvre « Déclarer : un problème ou une NC-S » (06/10) ; le temps du
  // choix, la barre passe au-dessus de l'en-tête : le + reste visible, en ×.
  const choix = useChoixDeclaration({ dansBarre: true })

  // Le bandeau reste de bord à bord (fond blanc + filet continus), mais son
  // CONTENU est plafonné comme celui de la page : sans ça, sur un écran large
  // les 5 items s'étalent sur toute la largeur pendant que le contenu est
  // centré — les cibles de clic se retrouvent aux antipodes du regard.
  const containerStyle: CSSProperties = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: 68,
    backgroundColor: 'var(--color-white)',
    borderTop: '1px solid var(--color-border)',
    zIndex: choix.affiche ? 72 : 30,
    paddingBottom: 'env(safe-area-inset-bottom, 0)',
  }

  const contenuStyle: CSSProperties = {
    height: '100%',
    maxWidth: 'var(--width-content)',
    marginInline: 'auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    alignItems: 'center',
  }

  return (
    <nav style={containerStyle} aria-label="Navigation principale">
    <div style={contenuStyle}>
      {/* 2 items gauche */}
      {ITEMS_LEFT.map((item) => (
        <NavItemLink key={item.to} item={item} />
      ))}

      {/* FAB central */}
      <div className="flex items-center justify-center">
        <button
          type="button"
          onClick={choix.basculer}
          aria-label={choix.ouvert ? 'Fermer' : 'Déclarer : un problème ou une NC-S'}
          aria-expanded={choix.ouvert}
          aria-haspopup="dialog"
          className="declarer-fab flex items-center justify-center rounded-full"
          style={{
            width: 56,
            height: 56,
            backgroundColor: 'var(--color-yellow)',
            color: 'var(--color-black)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
        >
          <Plus size={28} strokeWidth={2.5} />
        </button>
      </div>

      {/* 2 items droite */}
      {ITEMS_RIGHT.map((item) => (
        <NavItemLink key={item.to} item={item} />
      ))}
    </div>
    {choix.feuille}
    </nav>
  )
}

// ─── Item de navigation ───

function NavItemLink({ item }: { item: NavItem }): JSX.Element {
  const { Icon, label, to } = item

  return (
    <NavLink
      to={to}
      end={to === '/'}
      className="flex flex-col items-center justify-center gap-0.5 h-full"
      style={({ isActive }) => ({
        color: isActive ? 'var(--color-black)' : 'var(--color-gray-50)',
        textDecoration: 'none',
      })}
    >
      {({ isActive }) => (
        <>
          <Icon size={22} strokeWidth={isActive ? 2.2 : 1.8} />
          <span
            className="truncate max-w-full px-1"
            style={{
              fontSize: 'var(--font-size-info)',
              lineHeight: 'var(--line-height-info)',
              fontWeight: isActive ? 500 : 400,
            }}
          >
            {label}
          </span>
        </>
      )}
    </NavLink>
  )
}
